/**
 * components/admin/analytics/AdminKpiHeader.tsx
 *
 * Five KPI cards in funnel order:
 *   Sessions → Clients ajoutés → Tentatives d'envoi → Formulaires ouverts → Réponses obtenues
 *
 * Each card:
 *   - Uniform height via flex-col with fixed-height zones (header / value / subtitle / legend)
 *   - Shimmer skeleton matching real layout exactly (no reflow on load)
 *   - Count-up animation with 80 ms cascade stagger per card
 *   - Delta badge: hover tooltip shows "N actuel · M précédent · +X (vs. hier / vs. Xj)"
 *   - Funnel separator "›" between cards on xl+ screens
 *   - Conversion rate micro-legend on Ouverts and Réponses cards
 *
 * Updated May 2026: removed Formulaires créés. Added Tentatives d'envoi.
 * Funnel order. Cascade count-up. Uniform card height. Rich delta tooltip.
 * Removed period label from subtitles. Descriptive subtitles.
 */

import { useEffect, useRef, useState } from 'react';
import Card from '../../ui/Card';
import { Tooltip } from '../../ui/Tooltip';
import {
  Users, FileInput, CheckCircle2, TrendingUp, TrendingDown,
  Minus, UserPlus, Info, Send,
} from 'lucide-react';
import type { KpiDeltas, KpiPreviousValues, DateRange } from '../../../hooks/useAdminAnalytics';

interface AdminKpiHeaderProps {
  activeUsers: number;
  requestsSent: number;
  requestsOpened: number;
  requestsCompleted: number;
  clientsCreated: number;
  deltas: KpiDeltas;
  previousValues: KpiPreviousValues;
  loading: boolean;
  dateRange: DateRange;
}

// ── Skeleton shimmer ──────────────────────────────────────────────────────────
function Skel({ w, h }: { w: string; h: string }) {
  return <div className={`animate-pulse bg-neutral-100 rounded ${h} ${w}`} />;
}

// ── Staggered count-up ────────────────────────────────────────────────────────
function useCountUp(target: number, delay = 0, duration = 650): number {
  const [val, setVal] = useState(0);
  const raf = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (raf.current) cancelAnimationFrame(raf.current);
    setVal(0);
    timer.current = setTimeout(() => {
      let startTs: number | null = null;
      const animate = (ts: number) => {
        if (!startTs) startTs = ts;
        const p = Math.min((ts - startTs) / duration, 1);
        setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf.current = requestAnimationFrame(animate);
      };
      raf.current = requestAnimationFrame(animate);
    }, delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [target, delay, duration]);

  return val;
}

// ── Comparison label ──────────────────────────────────────────────────────────
function compLabel(dateRange: DateRange): string {
  return dateRange === 'today' ? 'hier' : `les ${dateRange} j précédents`;
}

// ── Delta badge with rich tooltip ─────────────────────────────────────────────
interface DeltaBadgeProps {
  delta: number | null;
  current: number;
  previous: number;
  unit?: string;
  good?: boolean;
  dateRange: DateRange;
}

function DeltaBadge({ delta, current, previous, unit = '', good = true, dateRange }: DeltaBadgeProps) {
  if (delta === null) return null;
  const neutral = delta === 0;
  const positive = delta > 0;
  const isGood = neutral ? null : positive === good;
  const cls = neutral
    ? 'bg-neutral-50 text-neutral-400'
    : isGood ? 'bg-success-50 text-success-600' : 'bg-error-50 text-error-600';
  const Icon = neutral ? Minus : positive ? TrendingUp : TrendingDown;

  const pctStr = previous > 0
    ? ` (${positive ? '+' : ''}${Math.round((delta / previous) * 100)}%)`
    : '';
  const tooltipText = `${current} actuellement · ${previous} ${compLabel(dateRange)}${pctStr}`;

  return (
    <Tooltip text={tooltipText} position="top">
      <span
        className={`inline-flex items-center gap-0.5 text-xs font-medium px-2 py-0.5 rounded-full cursor-help transition-transform duration-150 hover:scale-105 ${cls}`}
      >
        <Icon className="w-3 h-3" />
        {positive ? '+' : ''}{delta}{unit}
      </span>
    </Tooltip>
  );
}

// ── Conversion rate micro-legend ──────────────────────────────────────────────
function ConversionBadge({ rate, label }: { rate: number | null; label: string }) {
  if (rate === null) {
    return <span className="text-[11px] text-neutral-200 leading-none">—</span>;
  }
  const color = rate >= 60 ? 'text-success-500' : rate >= 30 ? 'text-amber-500' : 'text-neutral-400';
  return (
    <span className={`text-[11px] font-medium tabular-nums leading-none ${color}`}>
      {rate}% {label}
    </span>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
interface CardProps {
  title: string;
  tooltipText: string;
  rawValue: number;
  previousValue: number;
  suffix?: string;
  subtitle: string;
  icon: React.ReactNode;
  iconBg: string;
  loading: boolean;
  delta: number | null;
  deltaUnit?: string;
  deltaGood?: boolean;
  valueColor?: string;
  delay?: number;
  dateRange: DateRange;
  conversionRate?: number | null;
  conversionLabel?: string;
}

function KpiCard({
  title, tooltipText, rawValue, previousValue, suffix = '', subtitle,
  icon, iconBg, loading, delta, deltaUnit, deltaGood = true,
  valueColor = 'text-neutral-900', delay = 0, dateRange,
  conversionRate, conversionLabel = 'taux',
}: CardProps) {
  const animated = useCountUp(rawValue, delay);
  const hasConversion = conversionRate !== undefined;

  return (
    <Card className="flex flex-col h-full overflow-hidden hover:shadow-soft-lg transition-all duration-200 group">
      {/* Zone 1 — header: icon + delta badge */}
      <div className="flex items-start justify-between mb-3 flex-shrink-0">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-105 ${iconBg}`}>
          {icon}
        </div>
        <div className="h-6 flex items-center">
          {!loading && (
            <DeltaBadge
              delta={delta}
              current={rawValue}
              previous={previousValue}
              unit={deltaUnit}
              good={deltaGood}
              dateRange={dateRange}
            />
          )}
        </div>
      </div>

      {/* Zone 2 — value + subtitle + (i) (flex-1 so all cards share the same stretch) */}
      <div className="flex-1 flex flex-col justify-end">
        {loading ? (
          <div className="space-y-2">
            <Skel w="w-20" h="h-9" />
            <Skel w="w-36" h="h-3" />
          </div>
        ) : (
          <>
            <p className={`text-4xl font-bold font-serif leading-none tabular-nums ${valueColor}`}>
              {animated}{suffix}
            </p>
            <div className="flex items-center gap-1 mt-1.5">
              <p className="text-xs text-neutral-400 leading-tight">{subtitle}</p>
              <Tooltip text={tooltipText} position="top">
                <Info className="w-3 h-3 text-neutral-300 hover:text-neutral-500 cursor-help transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100 duration-150" />
              </Tooltip>
            </div>
          </>
        )}
      </div>

      {/* Zone 4 — conversion legend (always rendered, hidden when irrelevant) */}
      <div className="mt-2 flex-shrink-0 h-5 flex items-center">
        {loading ? (
          hasConversion ? <Skel w="w-16" h="h-3" /> : null
        ) : (
          hasConversion
            ? <ConversionBadge rate={conversionRate ?? null} label={conversionLabel} />
            : <span className="h-3" />
        )}
      </div>
    </Card>
  );
}

// ── Funnel separator ──────────────────────────────────────────────────────────
function FunnelSep() {
  return (
    <div className="hidden xl:flex items-start justify-center flex-shrink-0 pt-10 px-1">
      <span className="text-neutral-200 text-lg font-light select-none leading-none">›</span>
    </div>
  );
}

// ── Safe pct ─────────────────────────────────────────────────────────────────
function pct(num: number, denom: number): number | null {
  return denom > 0 ? Math.round((num / denom) * 100) : null;
}

// ── Export ────────────────────────────────────────────────────────────────────
export default function AdminKpiHeader({
  activeUsers, requestsSent, requestsOpened, requestsCompleted, clientsCreated,
  deltas, previousValues, loading, dateRange,
}: AdminKpiHeaderProps) {
  const openRate     = pct(requestsOpened,    requestsSent);
  const completeRate = pct(requestsCompleted, requestsOpened);

  const cards: CardProps[] = [
    {
      title: 'Sessions',
      tooltipText: "Une session démarre à la connexion ou après plus d'1h sans activité.",
      rawValue: activeUsers, previousValue: previousValues.activeUsers,
      subtitle: "Utilisateurs ayant ouvert l'app",
      icon: <Users className="w-4 h-4 text-primary-600" />, iconBg: 'bg-primary-50',
      loading, delta: deltas.activeUsers, deltaGood: true,
      delay: 0, dateRange,
    },
    {
      title: 'Clients ajoutés',
      tooltipText: "Nouveaux clients enregistrés sur la période, hors clients supprimés.",
      rawValue: clientsCreated, previousValue: previousValues.clientsCreated,
      subtitle: 'Nouveaux clients enregistrés',
      icon: <UserPlus className="w-4 h-4 text-teal-600" />, iconBg: 'bg-teal-50',
      loading, delta: deltas.clientsCreated, deltaGood: true,
      delay: 80, dateRange,
    },
    {
      title: "Tentatives d'envoi",
      tooltipText: "Nombre de clics sur « Copier le lien ». Chaque clic est comptabilisé.",
      rawValue: requestsSent, previousValue: previousValues.requestsSent,
      subtitle: 'Clics sur Copier le lien',
      icon: <Send className="w-4 h-4 text-sky-600" />, iconBg: 'bg-sky-50',
      loading, delta: deltas.requestsSent, deltaGood: true,
      delay: 160, dateRange,
    },
    {
      title: 'Formulaires ouverts',
      tooltipText: "Formulaires ouverts au moins une fois par le client destinataire.",
      rawValue: requestsOpened, previousValue: previousValues.requestsOpened,
      subtitle: 'Ouverts par le destinataire',
      icon: <FileInput className="w-4 h-4 text-blue-500" />, iconBg: 'bg-blue-50',
      loading, delta: deltas.requestsOpened, deltaGood: true,
      delay: 240, dateRange,
      conversionRate: openRate, conversionLabel: 'ouverture',
    },
    {
      title: 'Réponses obtenues',
      tooltipText: "Formulaires complétés et soumis par le client.",
      rawValue: requestsCompleted, previousValue: previousValues.requestsCompleted,
      subtitle: 'Formulaires complétés',
      icon: <CheckCircle2 className="w-4 h-4 text-success-600" />, iconBg: 'bg-success-50',
      loading, delta: deltas.requestsCompleted, deltaGood: true,
      delay: 320, dateRange,
      conversionRate: completeRate, conversionLabel: 'réponse',
      valueColor: requestsCompleted > 0 ? 'text-success-600' : 'text-neutral-900',
    },
  ];

  return (
    <>
      {/* Mobile / tablet: 2-column grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:hidden gap-3">
        {cards.map((c) => <KpiCard key={c.title} {...c} />)}
      </div>

      {/* XL+: horizontal funnel with separators, all cards same height via items-stretch */}
      <div className="hidden xl:flex items-stretch gap-0">
        {cards.map((c, i) => (
          <div key={c.title} className="contents">
            {i > 0 && <FunnelSep />}
            <div className="flex-1 min-w-0">
              <KpiCard {...c} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
